import React, { useState, useEffect } from 'react';
import { ServiceRequest } from '../types';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format, isToday, isTomorrow, parseISO, isBefore } from 'date-fns';
import { Check, X, Calendar, Bell, RefreshCw, CreditCard, ExternalLink, Ban, Star } from "lucide-react";
import { formatCurrency } from "@/utils/analyticsUtils";
import { toast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useProfileFetch } from "@/hooks/useProfileFetch";
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from "react-i18next";

interface RequestListProps {
  requests: ServiceRequest[];
  emptyMessage: string;
  showAcceptReject?: boolean;
  showComplete?: boolean;
  showReminder?: boolean;
  showAutoApproved?: boolean;
  showRequestReview?: boolean;
  onAccept?: (id: string) => Promise<boolean>;
  onReject?: (id: string, rejectionReason?: string) => Promise<boolean>;
  onComplete?: (id: string) => Promise<boolean>;
  onSendReminder?: (id: string) => Promise<{ success: boolean, error?: string }>;
  onRefresh?: () => void;
  onMarkAsPaid?: (id: string) => Promise<boolean>;
  onReschedule?: (requestId: string, newDate: Date) => Promise<void>;
  onRequestReview?: (id: string) => Promise<boolean>;
}

const RequestList: React.FC<RequestListProps> = ({
  requests,
  emptyMessage,
  showAcceptReject,
  showComplete,
  showReminder,
  showAutoApproved,
  showRequestReview,
  onAccept,
  onReject,
  onComplete,
  onSendReminder,
  onRefresh,
  onMarkAsPaid,
  onReschedule,
  onRequestReview
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState<{ [key: string]: boolean }>({});
  const { profile } = useProfileFetch();
  const navigate = useNavigate();
  const [cancelDialogOpen, setCancelDialogOpen] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [customCancelReason, setCustomCancelReason] = useState<string>("");
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [cancelConfirmStep, setCancelConfirmStep] = useState(false);
  
  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string>("");
  const [customRejectReason, setCustomRejectReason] = useState<string>("");
  const [rejectProcessing, setRejectProcessing] = useState(false);
  const [rejectConfirmStep, setRejectConfirmStep] = useState(false);
  
  const quickReasons = [
    t('cancelDialog.customerNoShow'),
    t('cancelDialog.customerCancelled'),
    t('cancelDialog.doubleBooking'),
    t('cancelDialog.safetyConcerns'),
    t('cancelDialog.emergency'),
    t('cancelDialog.other')
  ];
  
  // Quick reasons for rejection
  const rejectQuickReasons = [
    t('rejectDialog.fullyBooked'),
    t('rejectDialog.serviceNotAvailable'),
    t('rejectDialog.cannotMeetRequirements'),
    t('rejectDialog.scheduleConflict'),
    t('rejectDialog.equipmentUnavailable'),
    t('rejectDialog.tooShortNotice'),
    t('rejectDialog.other')
  ];

  const handleAccept = async (request: ServiceRequest) => {
    if (!onAccept) return;
    
    try {
      setIsProcessing(prev => ({ ...prev, [request.id]: true }));
      const success = await onAccept(request.id);
      
      if (success) {
        toast({
          title: t('common.success'),
          description: t('serviceRequestAccepted'),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('failedToAcceptRequest'),
      });
    } finally {
      setIsProcessing(prev => ({ ...prev, [request.id]: false }));
    }
  };

  const handleReject = async (request: ServiceRequest) => {
    // Open rejection dialog instead of directly rejecting
    setRejectDialogOpen(request.id);
    setRejectReason("");
    setCustomRejectReason("");
    setRejectConfirmStep(false);
  };

  const handleRejectWithReason = async (request: ServiceRequest) => {
    if (!rejectReason && !customRejectReason) {
      toast({
        variant: "destructive",
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription')
      });
      return;
    }
    if (!rejectConfirmStep) {
      setRejectConfirmStep(true);
      return;
    }
    if (!onReject) return;
    
    try {
      setRejectProcessing(true);
      const reason = rejectReason === t('rejectDialog.other') ? customRejectReason : rejectReason;
      const success = await onReject(request.id, reason);
      
      if (success) {
        toast({
          title: t('common.success'),
          description: t('serviceRequestRejected'),
        });
        setRejectDialogOpen(null);
        setRejectReason("");
        setCustomRejectReason("");
        setRejectConfirmStep(false);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('failedToRejectRequest'),
      });
    } finally {
      setRejectProcessing(false);
    }
  };

  const handleComplete = async (request: ServiceRequest) => {
    if (!onComplete) return;
    
    try {
      setIsProcessing(prev => ({ ...prev, [request.id]: true }));
      const success = await onComplete(request.id);
      
      if (success) {
        toast({
          title: t('common.success'),
          description: t('serviceMarkedCompleted'),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('failedToCompleteService'),
      });
    } finally {
      setIsProcessing(prev => ({ ...prev, [request.id]: false }));
    }
  };

  const handleMarkAsPaid = async (request: ServiceRequest) => {
    if (!onMarkAsPaid) return;
    
    try {
      setIsProcessing(prev => ({ ...prev, [request.id]: true }));
      const success = await onMarkAsPaid(request.id);
      
      if (success) {
        toast({
          title: t('common.success'),
          description: t('serviceMarkedPaid'),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('failedToMarkPaid'),
      });
    } finally {
      setIsProcessing(prev => ({ ...prev, [request.id]: false }));
    }
  };

  const handleRequestReview = async (request: ServiceRequest) => {
    if (!onRequestReview) return;
    
    try {
      setIsProcessing(prev => ({ ...prev, [request.id]: true }));
      const success = await onRequestReview(request.id);
      
      if (success) {
        toast({
          title: t('common.success'),
          description: t('requestActions.reviewRequestSent'),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('requestActions.reviewRequestError'),
      });
    } finally {
      setIsProcessing(prev => ({ ...prev, [request.id]: false }));
    }
  };

  const handleCancelRequest = async (request: ServiceRequest) => {
    if (!cancelReason && !customCancelReason) {
      toast({
        variant: "destructive",
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription')
      });
      return;
    }
    if (!cancelConfirmStep) {
      setCancelConfirmStep(true);
      return;
    }
    try {
      setCancelProcessing(true);
      const reason = cancelReason === t('cancelDialog.other') ? customCancelReason : cancelReason;
      const { error } = await supabase
        .from('service_requests')
        .update({ status: 'cancelled', cancellation_reason: reason })
        .eq('id', request.id);
      if (error) throw error;
      toast({ title: t('requestCancelled'), description: t('requestCancelledDescription') });
      setCancelDialogOpen(null);
      setCancelReason("");
      setCustomCancelReason("");
      setCancelConfirmStep(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast({ variant: "destructive", title: t('common.error'), description: t('failedToCancelRequest') });
    } finally {
      setCancelProcessing(false);
    }
  };

  const getStatusBadge = (status: string, scheduledAt: string | null) => {
    // For pending requests, check if the scheduled date has passed
    if (status === 'pending' && scheduledAt) {
      const scheduledDate = parseISO(scheduledAt);
      const now = new Date();
      if (isBefore(scheduledDate, now)) {
        return (
          <Badge className="bg-white text-gray-600 border border-gray-300">
            {t('navigation.expired')}
          </Badge>
        );
      }
    }
    
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800",
      accepted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
    };

    return (
      <Badge className={statusStyles[status as keyof typeof statusStyles]}>
        {t(`status.${status}`)}
      </Badge>
    );
  };

  const isExpired = (scheduledAt: string | null) => {
    if (!scheduledAt) return false;
    
    const scheduledDate = parseISO(scheduledAt);
    const now = new Date();
    return isBefore(scheduledDate, now);
  };

  // Improved formatting for dates
  const formatFullDate = (date: string) => {
    const parsedDate = parseISO(date);
    return format(parsedDate, "MMM d, yyyy");
  };

  const formatFullDateTime = (date: string) => {
    const parsedDate = parseISO(date);
    return format(parsedDate, "MMM d, yyyy 'at' h:mm a");
  };

  const formatRequestDate = (date: string) => {
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) {
      return t('navigation.today');
    } else if (isTomorrow(parsedDate)) {
      return t('navigation.tomorrow');
    } else {
      return format(parsedDate, "MMM d, yyyy");
    }
  };

  const canCompleteService = (request: ServiceRequest): boolean => {
    return request.status === 'accepted' && !request.paid;
  };

  // Use user's preferred currency from profile, or fallback to USD
  const currency = profile?.currency || "USD";

  // Function to navigate to request detail page
  const viewRequestDetails = (request: ServiceRequest) => {
    console.log("Navigating to request detail:", request.id);
    const url = `/dashboard/service-requests/${request.id}`;
    console.log("Target URL:", url);
    
    // Log event before navigation
    console.log("Starting navigation to:", url);
    
    // Use navigate from useNavigate hook
    navigate(url);
  };

  const formatDate = (dateString: string) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return t('navigation.today');
    } else if (isTomorrow(date)) {
      return t('navigation.tomorrow');
    }
    return format(date, "MMM d, yyyy");
  };

  const formatDateTime = (dateString: string) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return `${t('navigation.today')}, ${format(date, "h:mm a")}`;
    } else if (isTomorrow(date)) {
      return `${t('navigation.tomorrow')}, ${format(date, "h:mm a")}`;
    }
    return format(date, "MMM d, yyyy, h:mm a");
  };

  return (
    <div className="space-y-4">
      {requests.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {emptyMessage}
        </div>
      ) : (
        requests.map((request) => (
          <Card key={request.id} className="hover:shadow-md transition-shadow relative overflow-hidden border border-gray-200">
            <div className="absolute inset-0 cursor-pointer" onClick={() => viewRequestDetails(request)}></div>
            
            <div className="p-4 relative z-10">
              {/* Top section: Title and Price */}
              <div className="flex items-start justify-between mb-3">
                <div className="max-w-[70%]">
                  <h2 className="text-lg font-bold text-gray-900 text-left">{request.services.name}</h2>
                </div>
                <div className="text-right ml-4">
                  <p className="text-base font-semibold">{formatCurrency(request.services.price, currency)}</p>
                  <p className={request.paid ? "text-green-600 text-xs" : "text-red-600 text-xs"}>
                    {request.paid ? t('requestActions.paid') : t('requestActions.unpaid')}
                  </p>
                </div>
              </div>
              
              {/* Status badge */}
              <div className="mb-3">
                {getStatusBadge(request.status, request.scheduled_at)}
              </div>
              
              {/* Request details section */}
              <div className="mb-3">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm text-gray-600 text-left">
                    <span className="inline-block w-24">{t('navigation.requestedOn')} {formatDate(request.created_at)}</span>
                  </p>
                  
                  {request.scheduled_at && (
                    <p className="text-sm text-gray-600 text-left">
                      <span className="inline-block w-24">{t('navigation.serviceOn')} {formatDateTime(request.scheduled_at)}</span>
                    </p>
                  )}
                </div>
              </div>
              
              {/* Divider */}
              <div className="border-t border-gray-200 my-3"></div>
              
              {/* Contact Information */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-1 text-left">Contact Information</h3>
                <p className="text-sm text-gray-900 text-left">{request.customer_name}</p>
                {request.customer_email && (
                  <p className="text-sm text-gray-600 text-left">Email: {request.customer_email}</p>
                )}
                {request.customer_phone && (
                  <p className="text-sm text-gray-600 text-left">Phone: {request.customer_phone}</p>
                )}
              </div>
              
              {/* Notes (if any) */}
              {request.notes && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold mb-1 text-left">Notes</h3>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded text-left">{request.notes}</p>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex flex-row gap-2 mt-4 w-full">
                {/* Count how many buttons will be shown for this request */}
                {(() => {
                  const buttons = [];
                  // View button (always shown)
                  buttons.push(
                    <Button
                      key="view"
                      variant="outline"
                      className="flex-1 min-w-0 justify-center border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewRequestDetails(request);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('requestActions.view')}
                    </Button>
                  );
                  // Cancel button for accepted requests
                  if (request.status === 'accepted') {
                    buttons.push(
                      <Button
                        key="cancel"
                        variant="outline"
                        className="flex-1 min-w-0 justify-center border-red-400 text-red-700 bg-red-50 hover:bg-red-100"
                        onClick={e => {
                          e.stopPropagation();
                          setCancelDialogOpen(request.id);
                          setCancelReason("");
                          setCustomCancelReason("");
                        }}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        {t('requestActions.cancel')}
                      </Button>
                    );
                  }
                  // Mark Complete button
                  if (showComplete && canCompleteService(request)) {
                    buttons.push(
                      <Button
                        key="complete"
                        variant="outline"
                        className="flex-1 min-w-0 justify-center border-blue-400 text-blue-700 bg-blue-50 hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleComplete(request);
                        }}
                        disabled={isProcessing[request.id]}
                      >
                        {isProcessing[request.id] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        {t('requestActions.markComplete')}
                      </Button>
                    );
                  }
                  // Send Reminder button
                  if (
                    showReminder &&
                    request.status === 'accepted' &&
                    request.scheduled_at &&
                    new Date(request.scheduled_at) > new Date() &&
                    request.services.requires_scheduling
                  ) {
                    buttons.push(
                      <Button
                        key="reminder"
                        variant="outline"
                        className="flex-1 min-w-0 justify-center border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100"
                        onClick={() => onSendReminder(request.id)}
                        disabled={isProcessing[request.id]}
                      >
                        {isProcessing[request.id] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Bell className="h-4 w-4 mr-2" />
                        )}
                        {t('requestActions.sendReminder')}
                      </Button>
                    );
                  }
                  // Mark Paid button
                  if (
                    !request.paid &&
                    (request.status === 'accepted' || request.status === 'completed') &&
                    onMarkAsPaid
                  ) {
                    buttons.push(
                      <Button
                        key="paid"
                        variant="outline"
                        className="flex-1 min-w-0 justify-center border-green-500 text-green-700 bg-green-50 hover:bg-green-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsPaid(request);
                        }}
                        disabled={isProcessing[request.id]}
                      >
                        {isProcessing[request.id] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="h-4 w-4 mr-2" />
                        )}
                        {t('requestActions.markPaid')}
                      </Button>
                    );
                  }
                  // Request Review button for completed requests
                  if (
                    showRequestReview &&
                    request.status === 'completed' &&
                    onRequestReview
                  ) {
                    buttons.push(
                      <Button
                        key="review"
                        variant="outline"
                        className="flex-1 min-w-0 justify-center border-purple-500 text-purple-700 bg-purple-50 hover:bg-purple-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestReview(request);
                        }}
                        disabled={isProcessing[request.id]}
                      >
                        {isProcessing[request.id] ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Star className="h-4 w-4 mr-2" />
                        )}
                        {t('requestActions.requestReview')}
                      </Button>
                    );
                  }
                  return buttons;
                })()}
              </div>
              {/* Show cancellation reason if cancelled */}
              {request.status === 'cancelled' && request.cancellation_reason && (
                <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  <strong>Cancelled:</strong> {request.cancellation_reason}
                </div>
              )}
              {/* Show rejection reason if rejected */}
              {request.status === 'rejected' && request.rejection_reason && (
                <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  <strong>Rejected:</strong> {request.rejection_reason}
                </div>
              )}
              {/* Cancel Dialog */}
              <Dialog open={cancelDialogOpen === request.id} onOpenChange={open => { if (!open) { setCancelDialogOpen(null); setCancelConfirmStep(false); } }}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('cancelDialog.title')}</DialogTitle>
                    <DialogDescription>
                      {t('cancelDialog.description')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {quickReasons.map((reason) => (
                        <Button
                          key={reason}
                          variant={cancelReason === reason ? "default" : "outline"}
                          className={`text-xs px-3 py-1 ${cancelReason === reason ? 'border-2 border-red-600 ring-2 ring-red-200' : ''}`}
                          onClick={() => setCancelReason(reason)}
                        >
                          {reason}
                        </Button>
                      ))}
                    </div>
                    {(cancelReason === t('cancelDialog.other') || cancelReason === "") && (
                      <textarea
                        className="w-full border rounded p-2 text-sm"
                        placeholder={t('cancelDialog.enterCustomReason')}
                        value={customCancelReason}
                        onChange={e => setCustomCancelReason(e.target.value)}
                        rows={2}
                      />
                    )}
                    {cancelConfirmStep && (
                      <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm mt-2">
                        <strong>Warning:</strong> {t('cancelDialog.warningMessage')}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setCancelDialogOpen(null); setCancelConfirmStep(false); }} disabled={cancelProcessing}>
                      {t('cancelDialog.keepRequest')}
                    </Button>
                    <Button 
                      variant="destructive"
                      className="font-semibold bg-red-600 text-white hover:bg-red-700 border-red-600"
                      onClick={() => handleCancelRequest(request)}
                      disabled={cancelProcessing || (!cancelReason && !customCancelReason)}
                    >
                      {cancelProcessing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                      {t('cancelDialog.confirmCancel')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {/* Reject Dialog */}
              <Dialog open={rejectDialogOpen === request.id} onOpenChange={open => { if (!open) { setRejectDialogOpen(null); setRejectConfirmStep(false); } }}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('rejectDialog.title')}</DialogTitle>
                    <DialogDescription>
                      {t('rejectDialog.description')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {rejectQuickReasons.map((reason) => (
                        <Button
                          key={reason}
                          variant={rejectReason === reason ? "default" : "outline"}
                          className={`text-xs px-3 py-1 ${rejectReason === reason ? 'border-2 border-red-600 ring-2 ring-red-200' : ''}`}
                          onClick={() => setRejectReason(reason)}
                        >
                          {reason}
                        </Button>
                      ))}
                    </div>
                    {(rejectReason === t('rejectDialog.other') || rejectReason === "") && (
                      <textarea
                        className="w-full border rounded p-2 text-sm"
                        placeholder={t('rejectDialog.enterCustomReason')}
                        value={customRejectReason}
                        onChange={e => setCustomRejectReason(e.target.value)}
                        rows={2}
                      />
                    )}
                    {rejectConfirmStep && (
                      <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm mt-2">
                        <strong>Warning:</strong> {t('rejectDialog.warningMessage')}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setRejectDialogOpen(null); setRejectConfirmStep(false); }} disabled={rejectProcessing}>
                      {t('rejectDialog.cancel')}
                    </Button>
                    <Button 
                      variant="destructive"
                      className="font-semibold bg-red-600 text-white hover:bg-red-700 border-red-600"
                      onClick={() => handleRejectWithReason(request)}
                      disabled={rejectProcessing || (!rejectReason && !customRejectReason)}
                    >
                      {rejectProcessing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                      {t('rejectDialog.confirmReject')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

export default RequestList;
